import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, User, MoreHorizontal, Pencil, Trash2, Plus, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useUser } from '../../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Tarjeta de unidad TYPSA individual
 */
export const TypsaUnitCard = ({ unit, onEdit, onDelete, onUpdated, isEditor, isAdmin, showOwner = true }) => {
  const navigate = useNavigate();
  const { currentUser, editorUsers } = useUser();
  const [owner, setOwner] = useState(unit.owner || '__none__');

  useEffect(() => {
    setOwner(unit.owner || '__none__');
  }, [unit.owner]);

  const handleOwnerChange = async (val) => {
    setOwner(val);
    try {
      const newValue = val === '__none__' ? null : val;
      // PUT requires full TypsaOrgUnitBase body
      await axios.put(
        `${API}/typsa-org-units/${unit.id}`,
        {
          category: unit.category,
          value: unit.value,
          owner: newValue,
          description: unit.description || null,
          website: unit.website || null,
          notes: unit.notes || null,
        },
        { headers: { 'X-User-Email': currentUser?.email } }
      );
      toast.success('Responsable actualizado');
      onUpdated?.();
    } catch (err) {
      console.error(err);
      toast.error('Error al actualizar responsable');
      setOwner(unit.owner || '__none__');
    }
  };

  return (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/typsa-org-units/${unit.id}`)}
      data-testid={`typsa-unit-card-${unit.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-medium tracking-tight text-blue-800 dark:text-blue-200">
            {unit.value}
          </CardTitle>
          {(isEditor || isAdmin) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(unit); }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onDelete(unit); }}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          {unit.description && (
            <div className="text-muted-foreground text-xs">
              {unit.description}
            </div>
          )}
          {/* Quick-edit owner dropdown for editors */}
          {showOwner && isEditor ? (
            <div
              className="flex items-center gap-2 pt-2 border-t"
              onClick={(e) => e.stopPropagation()}
              data-testid={`typsa-unit-owner-block-${unit.id}`}
            >
              <User className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
              <Select value={owner} onValueChange={handleOwnerChange}>
                <SelectTrigger
                  className="h-8 text-xs"
                  data-testid={`typsa-unit-owner-select-${unit.id}`}
                >
                  <SelectValue placeholder="Sin responsable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin responsable</SelectItem>
                  {(editorUsers || []).map((u) => (
                    <SelectItem key={u.email} value={u.name}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            showOwner && unit.owner && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <User className="w-3 h-3" />
                <span>Resp: {unit.owner}</span>
              </div>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Sección TYPSA completa (Header + Categorías + Unidades)
 */
export const TypsaSection = ({
  isExpanded,
  onToggle,
  filteredTypsaCount,
  typsaGroupByOwner,
  setTypsaGroupByOwner,
  typsaCategories,
  expandedGroups,
  expandedOwnerGroups,
  onToggleGroup,
  onToggleOwnerGroup,
  onEditUnit,
  onDeleteUnit,
  onNewUnit,
  onUpdated,
  isEditor,
  isAdmin
}) => {
  return (
    <div className="border rounded-xl overflow-hidden">
      {/* TYPSA Header */}
      <div 
        className="py-3 px-4 cursor-pointer transition-colors bg-blue-500 bg-opacity-10 hover:bg-opacity-20"
        onClick={() => onToggle('typsa')}
        data-testid="company-type-typsa"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            {isExpanded 
              ? <ChevronDown className="w-5 h-5" />
              : <ChevronRight className="w-5 h-5" />
            }
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <h2 className="text-lg font-bold">TYPSA</h2>
            <Badge variant="outline" className="ml-1">
              {filteredTypsaCount} unidad{filteredTypsaCount !== 1 ? 'es' : ''}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <label 
              className="flex items-center gap-2 text-sm cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={typsaGroupByOwner}
                onChange={(e) => setTypsaGroupByOwner(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-muted-foreground">Agrupar por responsable</span>
            </label>
            {isAdmin && (
              <Button 
                size="sm" 
                className="h-8 bg-blue-500 text-white hover:bg-blue-600 shadow-sm"
                onClick={(e) => { e.stopPropagation(); onNewUnit(); }}
                data-testid="add-typsa-unit-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva Unidad
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* TYPSA Content - Categories */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-blue-50/30 dark:bg-blue-950/20">
          {typsaCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No hay unidades organizativas</p>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={onNewUnit}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Crear primera unidad
                </Button>
              )}
            </div>
          ) : (
            typsaCategories.map((category) => (
              <TypsaCategoryContent
                key={`typsa-${category.key}`}
                category={category}
                expandedGroups={expandedGroups}
                expandedOwnerGroups={expandedOwnerGroups}
                onToggleGroup={onToggleGroup}
                onToggleOwnerGroup={onToggleOwnerGroup}
                typsaGroupByOwner={typsaGroupByOwner}
                onEditUnit={onEditUnit}
                onDeleteUnit={onDeleteUnit}
                onUpdated={onUpdated}
                isEditor={isEditor}
                isAdmin={isAdmin}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Contenido de una categoría TYPSA (División, Departamento, etc.)
 */
export const TypsaCategoryContent = ({
  category,
  expandedGroups,
  expandedOwnerGroups,
  onToggleGroup,
  onToggleOwnerGroup,
  typsaGroupByOwner,
  onEditUnit,
  onDeleteUnit,
  onUpdated,
  isEditor,
  isAdmin
}) => {
  const catKey = `typsa-${category.key}`;
  const isCatExpanded = expandedGroups[catKey] ?? false;
  
  return (
    <div>
      {/* Category Header */}
      <div 
        className="py-2 px-3 border rounded-lg bg-background cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => onToggleGroup(catKey)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isCatExpanded 
              ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
              : <ChevronRight className="w-4 h-4 text-muted-foreground" />
            }
            <span className="font-semibold text-blue-700 dark:text-blue-300">{category.label}</span>
            <span className="text-xs text-muted-foreground">({category.totalUnits})</span>
          </div>
        </div>
      </div>
      
      {/* Units Content */}
      {isCatExpanded && (
        <div className="mt-3 ml-6">
          {category.groupedByOwner ? (
            // Grouped by owner view
            <TypsaOwnerGroups
              category={category}
              expandedOwnerGroups={expandedOwnerGroups}
              onToggleOwnerGroup={onToggleOwnerGroup}
              onEditUnit={onEditUnit}
              onDeleteUnit={onDeleteUnit}
              onUpdated={onUpdated}
              isEditor={isEditor}
              isAdmin={isAdmin}
            />
          ) : (
            // Alphabetical view (default)
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {category.units.map((unit) => (
                <TypsaUnitCard
                  key={unit.id}
                  unit={unit}
                  onEdit={onEditUnit}
                  onDelete={onDeleteUnit}
                  onUpdated={onUpdated}
                  isEditor={isEditor}
                  isAdmin={isAdmin}
                  showOwner={true}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Grupos de unidades TYPSA por responsable
 */
export const TypsaOwnerGroups = ({
  category,
  expandedOwnerGroups,
  onToggleOwnerGroup,
  onEditUnit,
  onDeleteUnit,
  onUpdated,
  isEditor,
  isAdmin
}) => {
  return (
    <div className="space-y-2">
      {category.ownerGroups.map((ownerGroup) => {
        const ownerKey = `${category.key}-${ownerGroup.owner}`;
        const isOwnerExpanded = expandedOwnerGroups[ownerKey] ?? false;
        
        return (
          <div key={ownerGroup.owner}>
            <div 
              className="flex items-center gap-2 py-2 px-3 text-sm cursor-pointer rounded-lg hover:bg-muted/50 transition-colors"
              onClick={() => onToggleOwnerGroup(ownerKey)}
            >
              {isOwnerExpanded 
                ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                : <ChevronRight className="w-4 h-4 text-muted-foreground" />
              }
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{ownerGroup.owner}</span>
              <span className="text-xs text-muted-foreground">({ownerGroup.units.length})</span>
            </div>
            {isOwnerExpanded && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2 ml-6">
                {ownerGroup.units.map((unit) => (
                  <TypsaUnitCard
                    key={unit.id}
                    unit={unit}
                    onEdit={onEditUnit}
                    onDelete={onDeleteUnit}
                    onUpdated={onUpdated}
                    isEditor={isEditor}
                    isAdmin={isAdmin}
                    showOwner={false}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
